import React, { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import useIsMobile from '../utils/useIsMobile';

// ─────────────────────────────────────────────────────────────────────────────
// Agent / Admin View
// ─────────────────────────────────────────────────────────────────────────────
function AgentChat({ user, token }) {
  const isMobile = useIsMobile();
  const [socket, setSocket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketSearch, setTicketSearch] = useState('');
  const [loadingTickets, setLoadingTickets] = useState(true);

  // Active chat state
  const [sessionId, setSessionId] = useState(null);
  const [chatInfo, setChatInfo] = useState(null);   // { customerName, ticketNumber, ticketTitle, customerOnline }
  const [status, setStatus] = useState('idle');     // idle | initiated | connected | ended
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [onlineAgents, setOnlineAgents] = useState(0);
  const msgEnd = useRef(null);

  // Load tickets list
  const loadTickets = useCallback(async () => {
    try {
      const data = await apiFetch('/chat/tickets');
      setTickets(data);
    } catch {}
    setLoadingTickets(false);
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  useEffect(() => {
    const s = io('/', { auth: { token }, transports: ['websocket', 'polling'] });
    setSocket(s);

    s.on('agents:online', n => setOnlineAgents(n));

    // Agent initiated, waiting for customer
    s.on('chat:initiated', ({ sessionId: sid, customerName, ticketNumber, ticketTitle, customerOnline }) => {
      setSessionId(sid);
      setChatInfo({ customerName, ticketNumber, ticketTitle, customerOnline });
      setStatus(customerOnline ? 'initiated' : 'initiated');
      setMessages([{
        id: 'sys-1', system: true,
        content: customerOnline
          ? `Chat request sent to ${customerName}. Waiting for them to join…`
          : `${customerName} is offline. They will see this chat when they come online.`
      }]);
    });

    s.on('chat:connected', ({ customerName }) => {
      setStatus('connected');
      if (customerName) {
        setMessages(m => [...m, { id: Date.now(), system: true, content: `${customerName} joined the chat.` }]);
      }
    });

    s.on('chat:history', (history) => {
      setMessages(history.map(m => ({ id: m.id, senderId: m.senderId, senderName: m.senderName, content: m.content })));
    });

    s.on('chat:message', msg => setMessages(m => [...m, msg]));

    s.on('chat:ended', () => {
      setStatus('ended');
      setMessages(m => [...m, { id: Date.now(), system: true, content: 'Chat session ended.' }]);
    });

    return () => s.disconnect();
  }, [token]);

  useEffect(() => { msgEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  function startChat(ticket) {
    if (!socket) return;
    setMessages([]);
    setStatus('idle');
    socket.emit('chat:initiate', { ticketId: ticket.id, customerId: ticket.customer_id });
  }

  function sendMsg(e) {
    e.preventDefault();
    if (!input.trim()) return;
    socket?.emit('chat:message', { content: input });
    setInput('');
  }

  function endChat() {
    socket?.emit('chat:end');
  }

  function resetChat() {
    setSessionId(null);
    setChatInfo(null);
    setStatus('idle');
    setMessages([]);
  }

  const filtered = tickets.filter(t => {
    const q = ticketSearch.toLowerCase();
    return !q || t.ticket_number?.toLowerCase().includes(q) ||
      t.customer_name?.toLowerCase().includes(q) ||
      t.title?.toLowerCase().includes(q);
  });

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '14px 10px 40px' : '32px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 8, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Live Chat</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Select a complaint to start a chat with the customer</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: onlineAgents > 0 ? '#0d7a28' : '#9ca3af', display: 'inline-block' }} />
          <span style={{ color: 'var(--text-secondary)' }}>{onlineAgents} agent{onlineAgents !== 1 ? 's' : ''} online</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '380px 1fr', gap: isMobile ? 14 : 20, alignItems: 'start' }}>

        {/* Left — ticket selector */}
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Open Complaints</p>
            <input className="input" style={{ fontSize: 13 }} placeholder="Search by ticket, customer…"
              value={ticketSearch} onChange={e => setTicketSearch(e.target.value)} />
          </div>
          <div style={{ maxHeight: isMobile ? 240 : 520, overflowY: 'auto' }}>
            {loadingTickets ? (
              <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>
            ) : filtered.length === 0 ? (
              <p style={{ padding: 24, color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center' }}>No open complaints.</p>
            ) : (
              filtered.map(t => {
                const isActive = t.id === (chatInfo && sessionId ? tickets.find(tk => tk.active_session_id === sessionId)?.id : null);
                const hasSession = !!t.active_session_id;
                return (
                  <div key={t.id}
                    onClick={() => startChat(t)}
                    style={{
                      padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                      background: isActive ? 'var(--n-100)' : 'var(--bg-surface)', transition: 'background .1s'
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-app)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-surface)'; }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, background: '#0B6623', color: '#fff', padding: '1px 6px', borderRadius: 4 }}>
                            {t.ticket_number}
                          </span>
                          {hasSession && (
                            <span style={{ fontSize: 11, background: 'var(--action-bg)', color: 'var(--status-resolved-text)', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>
                              Active
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{t.title}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          👤 {t.customer_name}
                          {t.product_name && <> · 📦 {t.product_name}</>}
                        </p>
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap', background: 'var(--n-100)', padding: '2px 7px', borderRadius: 4, flexShrink: 0 }}>
                        {t.status}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right — chat window */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 480 : 580 }}>
          {/* Header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              {!chatInfo ? (
                <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>← Select a complaint to start chatting</span>
              ) : (
                <>
                  <p style={{ fontWeight: 600, fontSize: 15 }}>
                    💬 {chatInfo.customerName}
                  </p>
                  {chatInfo.ticketNumber && (
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {chatInfo.ticketNumber} · {chatInfo.ticketTitle}
                    </p>
                  )}
                </>
              )}
            </div>
            {status !== 'idle' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 999, fontWeight: 500,
                  background: status === 'connected' ? 'var(--status-resolved-bg)' : status === 'initiated' ? 'var(--status-prog-bg)' : 'var(--n-100)',
                  color: status === 'connected' ? '#094d1e' : status === 'initiated' ? '#92400e' : 'var(--text-secondary)' }}>
                  {status === 'connected' ? '● Connected' : status === 'initiated' ? '⏳ Waiting' : 'Ended'}
                </span>
                <button className="btn btn-sm" style={{ background: 'var(--red-50)', color: '#b91c1c' }} onClick={resetChat}>Close</button>
              </div>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!chatInfo && (
              <div style={{ textAlign: 'center', marginTop: 80 }}>
                <div style={{ fontSize: 52, marginBottom: 12 }}>💬</div>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Select a complaint from the left to start a chat with the customer.</p>
              </div>
            )}
            {messages.map(m => <ChatBubble key={m.id} msg={m} userId={user.id} />)}
            <div ref={msgEnd} />
          </div>

          {/* Input */}
          {status === 'connected' && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)' }}>
              <ChatInput input={input} setInput={setInput} onSend={sendMsg} onEnd={endChat} />
            </div>
          )}
          {status === 'ended' && (
            <div style={{ padding: 16, borderTop: '1px solid var(--border-subtle)', textAlign: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={resetChat}>Start Another Chat</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Customer View
// ─────────────────────────────────────────────────────────────────────────────
function CustomerChat({ user, token }) {
  const isMobile = useIsMobile();
  const [socket, setSocket] = useState(null);
  const [status, setStatus] = useState('idle');    // idle | incoming | connected | ended
  const [incoming, setIncoming] = useState(null);  // { sessionId, agentName, ticketNumber, ticketTitle }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [agentName, setAgentName] = useState('');
  const [onlineAgents, setOnlineAgents] = useState(0);
  const msgEnd = useRef(null);

  useEffect(() => {
    const s = io('/', { auth: { token }, transports: ['websocket', 'polling'] });
    setSocket(s);

    s.on('agents:online', n => setOnlineAgents(n));

    // Agent started a chat with this customer
    s.on('chat:incoming', ({ sessionId, agentName: an, ticketNumber, ticketTitle }) => {
      setIncoming({ sessionId, agentName: an, ticketNumber, ticketTitle });
      setStatus('incoming');
    });

    s.on('chat:connected', ({ agentName: an }) => {
      setStatus('connected');
      if (an) setAgentName(an);
    });

    s.on('chat:history', history => {
      setMessages(history.map(m => ({ id: m.id, senderId: m.senderId, senderName: m.senderName, content: m.content })));
    });

    s.on('chat:message', msg => setMessages(m => [...m, msg]));

    s.on('chat:ended', () => {
      setStatus('ended');
      setMessages(m => [...m, { id: Date.now(), system: true, content: 'Chat ended by support agent.' }]);
    });

    s.on('chat:queued', () => setStatus('initiated'));

    return () => s.disconnect();
  }, [token]);

  useEffect(() => { msgEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Check for pending sessions on mount
  useEffect(() => {
    apiFetch('/chat/my-sessions').then(sessions => {
      if (sessions.length > 0 && status === 'idle') {
        const s = sessions[0];
        setIncoming({ sessionId: s.id, agentName: s.agent_name, ticketNumber: s.ticket_number, ticketTitle: s.title });
        setStatus('incoming');
      }
    }).catch(() => {});
  }, []);

  function joinChat() {
    if (!incoming) return;
    socket?.emit('chat:join', { sessionId: incoming.sessionId });
    setMessages([]);
  }

  function declineChat() {
    setIncoming(null);
    setStatus('idle');
  }

  function sendMsg(e) {
    e.preventDefault();
    if (!input.trim()) return;
    socket?.emit('chat:message', { content: input });
    setInput('');
  }

  function endChat() {
    socket?.emit('chat:end');
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: isMobile ? '14px 10px 40px' : '32px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 8, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Live Chat Support</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Chat with our support team.</p>
        </div>
        <span style={{ fontSize: 12, color: onlineAgents > 0 ? '#0d7a28' : 'var(--text-secondary)' }}>
          ● {onlineAgents} agent{onlineAgents !== 1 ? 's' : ''} online
        </span>
      </div>

      {/* Incoming chat notification */}
      {status === 'incoming' && incoming && (
        <div style={{ background: 'var(--status-resolved-bg)', border: '2px solid var(--status-resolved-text)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ fontSize: 32, flexShrink: 0 }}>💬</div>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: 'var(--status-resolved-text)', marginBottom: 4 }}>
                Support agent {incoming.agentName ? `"${incoming.agentName}"` : ''} wants to chat with you
              </p>
              {incoming.ticketNumber && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Regarding complaint <strong>{incoming.ticketNumber}</strong>{incoming.ticketTitle ? ` — ${incoming.ticketTitle}` : ''}
                </p>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={joinChat}>Join Chat</button>
                <button className="btn btn-secondary" onClick={declineChat}>Dismiss</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 480 : 500 }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>
            {status === 'connected' ? `Connected with ${agentName}` :
             status === 'ended' ? 'Chat Ended' : 'Live Chat'}
          </span>
          {status === 'connected' && (
            <span style={{ fontSize: 12, background: 'var(--status-resolved-bg)', color: 'var(--status-resolved-text)', padding: '3px 10px', borderRadius: 999, fontWeight: 500 }}>
              ● Connected
            </span>
          )}
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(status === 'idle') && (
            <div style={{ textAlign: 'center', marginTop: 60 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 8 }}>
                Your support agent will initiate a chat with you from your complaint.
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Keep this page open to receive chat requests.</p>
            </div>
          )}
          {messages.map(m => <ChatBubble key={m.id} msg={m} userId={user.id} />)}
          <div ref={msgEnd} />
        </div>

        {/* Input */}
        {status === 'connected' && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)' }}>
            <ChatInput input={input} setInput={setInput} onSend={sendMsg} onEnd={endChat} />
          </div>
        )}
        {status === 'ended' && (
          <div style={{ padding: 16, borderTop: '1px solid var(--border-subtle)', textAlign: 'center' }}>
            <button className="btn btn-secondary" onClick={() => { setStatus('idle'); setMessages([]); }}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Root export — picks the right view
// ─────────────────────────────────────────────────────────────────────────────
export default function LiveChat() {
  const { user, token, isAgent } = useAuth();
  if (isAgent) return <AgentChat user={user} token={token} />;
  return <CustomerChat user={user} token={token} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared components
// ─────────────────────────────────────────────────────────────────────────────
function ChatBubble({ msg, userId }) {
  if (msg.system) return (
    <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)', padding: '4px 0' }}>{msg.content}</div>
  );
  const mine = msg.senderId === userId;
  return (
    <div style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 8, alignItems: 'flex-end' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: mine ? 'var(--status-resolved-bg)' : 'var(--n-200)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
        {msg.senderName?.[0]?.toUpperCase()}
      </div>
      <div style={{ maxWidth: '65%' }}>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3, textAlign: mine ? 'right' : 'left' }}>
          {msg.senderName}
        </div>
        <div style={{ background: mine ? 'var(--status-resolved-bg)' : 'var(--n-100)', color: 'var(--text-primary)', padding: '8px 12px', borderRadius: 10, fontSize: 14, lineHeight: 1.4, wordBreak: 'break-word' }}>
          {msg.content}
        </div>
      </div>
    </div>
  );
}

function ChatInput({ input, setInput, onSend, onEnd }) {
  return (
    <form onSubmit={onSend} style={{ display: 'flex', gap: 8 }}>
      <input className="input" placeholder="Type a message…" value={input}
        onChange={e => setInput(e.target.value)} style={{ flex: 1 }} autoFocus />
      <button className="btn btn-primary" type="submit" disabled={!input.trim()}>Send</button>
      <button className="btn btn-secondary" type="button" onClick={onEnd}>End</button>
    </form>
  );
}
